import { useMutation, type ApolloError, type TypedDocumentNode } from '@apollo/client';
import { Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import type { BulkUpdateResult } from '~/generated/graphql';
import { usePersistentSnackbar, type ApiOptions } from '../helpers';
import { BulkUpdateDevicesDocument } from './bulk-update.generated';

interface BulkUpdatePayload {
  bulkUpdateDevices?:
    | {
        devices: readonly unknown[];
        result: BulkUpdateResult;
      }
    | null
    | undefined;
}

// This makes result-aware toasts for bulk device updates.
// It requires that `devices` and `errors` are included in the payload.
const useNotifications = <T extends BulkUpdatePayload, U>(_document: TypedDocumentNode<T, U>) => {
  const { enqueueSnackbar } = useSnackbar();
  const persistentSnackbar = usePersistentSnackbar();
  const { t } = useTranslation();

  const showMessage = (message: string, variant: 'success' | 'warning' = 'success') =>
    enqueueSnackbar(<Typography color="inherit">{message}</Typography>, { variant });

  const showError = (message: string) => persistentSnackbar(message, { reload: true });

  return {
    onCompleted: ({ bulkUpdateDevices }: T) => {
      if (!bulkUpdateDevices?.devices.length) return showError(t('api.common.error_generic'));

      const succeeded = bulkUpdateDevices.result.succeeded;
      const count = succeeded;
      showMessage(t('api.devices.bulk_update_success_plural', { count }));
    },
    onError: (_error: ApolloError) => showError(t('api.common.error_generic')),
  };
};

// We may want to have different payloads depending on what we're bulk updating.
// We may want to have a form-based bulk update as well.
// This makes it easy to create variants.
const makeApi =
  <T extends BulkUpdatePayload, U>(document: TypedDocumentNode<T, U>) =>
  (options: ApiOptions<typeof document> = {}) =>
    useMutation(document, { ...useNotifications(document), ...options });

export const useBulkUpdateDevices = makeApi(BulkUpdateDevicesDocument);
